// side panel replacing bottom drawer — Google Calendar-style day detail

import React, { useMemo } from "react";
import { Typography, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarCheck, faClipboardCheck, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { getStatusChipProps, formatQuantity } from "../../utils/scheduleUtils";
import "../../style/ScheduleDayPanel.css";

// parse "HH:MM:SS" → fractional hours from midnight
function timeToHours(raw) {
  if (!raw) return null;
  const [h, m] = String(raw).split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h + (m || 0) / 60;
}

function ScheduleDayPanel({
  year,
  month,
  day,
  events = [],
  darkMode = false,
  closing = false,
  onClose,
  onScheduleRow,
  onConfirmRow,
}) {
  const { t, i18n } = useTranslation();

  // localized date header
  const dateLabel = useMemo(() => {
    try {
      const d = new Date(year, month, day);
      return new Intl.DateTimeFormat(i18n.language, {
        weekday: "long",
        month: "long",
        day: "numeric",
      }).format(d);
    } catch {
      return `${day}`;
    }
  }, [year, month, day, i18n.language]);

  // split into timed vs all-day events
  const { timed, allDay } = useMemo(() => {
    const t = [];
    const a = [];
    for (const ev of events) {
      const startH = timeToHours(ev.timeStart);
      const endH = timeToHours(ev.timeEnd);
      if (startH !== null && endH !== null && endH > startH) {
        t.push({ ...ev, startH, endH });
      } else {
        a.push(ev);
      }
    }
    // sort timed by start
    t.sort((a, b) => a.startH - b.startH);
    return { timed: t, allDay: a };
  }, [events]);

  // timeline hour range — clamp to event bounds with 1h padding
  const { hourStart, hourEnd, hourHeight } = useMemo(() => {
    if (timed.length === 0) return { hourStart: 8, hourEnd: 18, hourHeight: 60 };
    let lo = 24, hi = 0;
    for (const ev of timed) {
      if (ev.startH < lo) lo = ev.startH;
      if (ev.endH > hi) hi = ev.endH;
    }
    const s = Math.max(0, Math.floor(lo) - 1);
    const e = Math.min(24, Math.ceil(hi) + 1);
    return { hourStart: s, hourEnd: e, hourHeight: 60 };
  }, [timed]);

  // detect overlapping blocks and assign columns
  const timedWithCols = useMemo(() => {
    if (timed.length === 0) return [];

    const sorted = [...timed];
    const cols = new Array(sorted.length).fill(0);
    const maxCol = new Array(sorted.length).fill(0);

    // greedy left-to-right column assignment
    for (let i = 0; i < sorted.length; i++) {
      let col = 0;
      for (let j = 0; j < i; j++) {
        // overlaps if j hasn't ended before i starts
        if (sorted[j].endH > sorted[i].startH) {
          if (cols[j] === col) col++;
        }
      }
      cols[i] = col;
    }

    // compute total columns per overlap group
    for (let i = 0; i < sorted.length; i++) {
      let groupMax = cols[i];
      for (let j = 0; j < sorted.length; j++) {
        if (i === j) continue;
        const overlaps =
          sorted[j].startH < sorted[i].endH &&
          sorted[j].endH > sorted[i].startH;
        if (overlaps && cols[j] > groupMax) groupMax = cols[j];
      }
      maxCol[i] = groupMax;
    }

    return sorted.map((ev, i) => ({
      ...ev,
      col: cols[i],
      totalCols: maxCol[i] + 1,
    }));
  }, [timed]);

  const totalHours = hourEnd - hourStart;
  const timelineH = totalHours * hourHeight;

  // is this today?
  const isToday = useMemo(() => {
    const now = new Date();
    return year === now.getFullYear() && month === now.getMonth() && day === now.getDate();
  }, [year, month, day]);

  // now indicator position
  const nowOffset = useMemo(() => {
    if (!isToday) return null;
    const now = new Date();
    const h = now.getHours() + now.getMinutes() / 60;
    if (h < hourStart || h > hourEnd) return null;
    return (h - hourStart) * hourHeight;
  }, [isToday, hourStart, hourEnd, hourHeight]);

  const renderEventBlock = (ev, style, isAllDay) => {
    const chipProps = getStatusChipProps(ev.status);
    const canSchedule = ev.status === "pending";
    const canConfirm = ev.status === "scheduled" || ev.status === "late";

    return (
      <div
        key={ev.requestId || ev.id}
        className={`day-panel__block day-panel__block--${ev.status}${isAllDay ? " day-panel__block--allday" : ""}`}
        style={style}
      >
        <div className="day-panel__block-main">
          <span className="day-panel__block-material">{ev.material}</span>
          <Chip
            size="small"
            label={
              chipProps.hasAlert
                ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                    {t(chipProps.labelKey, chipProps.fallback)}
                    <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: "0.58rem" }} />
                  </span>
                : t(chipProps.labelKey, chipProps.fallback)
            }
            variant={chipProps.variant}
            color={chipProps.color}
            sx={{ fontWeight: 800, fontSize: "0.62rem", height: 20 }}
          />
        </div>
        <div className="day-panel__block-meta">
          <span>{ev.donor}</span>
          <span className="day-panel__block-qty">
            {formatQuantity(ev.allocatedQty)} {t("schedule.calendar.qty", "qty")}
          </span>
        </div>
        {ev.timeWindow && ev.timeWindow !== "—" && isAllDay && (
          <div className="day-panel__block-time">{ev.timeWindow}</div>
        )}
        {(canSchedule || canConfirm) && (
          <button
            className={`day-panel__action day-panel__action--${canSchedule ? "primary" : "danger"}`}
            onClick={(e) => {
              e.stopPropagation();
              if (canSchedule) onScheduleRow?.(ev);
              else onConfirmRow?.(ev);
            }}
            title={canSchedule
              ? t("schedule.actions.schedule", "Confirm pickup")
              : t("schedule.actions.confirmReception", "Confirm reception")}
          >
            <FontAwesomeIcon icon={canSchedule ? faCalendarCheck : faClipboardCheck} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`day-panel ${darkMode ? "dark" : "light"}${closing ? " day-panel--closing" : ""}`}>
      <div className="day-panel__header">
        <div className="day-panel__header-left">
          <Typography className="day-panel__date">{dateLabel}</Typography>
          <span className="day-panel__count">
            {events.length} {events.length === 1
              ? t("schedule.dayPanel.pickup", "pickup")
              : t("schedule.dayPanel.pickups", "pickups")}
          </span>
        </div>
        <button
          className="day-panel__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="day-panel__body">
        {/* all-day / no-time events */}
        {allDay.length > 0 && (
          <div className="day-panel__allday-section">
            <span className="day-panel__section-label">
              {t("schedule.dayPanel.allDay", "All day")}
            </span>
            <div className="day-panel__allday-items">
              {allDay.map((ev) => renderEventBlock(ev, {}, true))}
            </div>
          </div>
        )}

        {/* timeline */}
        {timed.length > 0 && (
          <div className="day-panel__timeline">
            <div className="day-panel__hours" style={{ height: timelineH }}>
              {Array.from({ length: totalHours + 1 }, (_, i) => {
                const hr = hourStart + i;
                if (hr > 23) return null;
                const label = hr === 0 ? "12 AM"
                  : hr < 12 ? `${hr} AM`
                  : hr === 12 ? "12 PM"
                  : `${hr - 12} PM`;
                return (
                  <div
                    key={hr}
                    className="day-panel__hour-row"
                    style={{ top: i * hourHeight }}
                  >
                    <span className="day-panel__hour-label">{label}</span>
                    <div className="day-panel__hour-line" />
                  </div>
                );
              })}

              {/* now indicator */}
              {nowOffset !== null && (
                <div className="day-panel__now" style={{ top: nowOffset }}>
                  <div className="day-panel__now-dot" />
                  <div className="day-panel__now-line" />
                </div>
              )}

              {/* timed event blocks */}
              <div className="day-panel__blocks">
                {timedWithCols.map((ev) => {
                  const top = (ev.startH - hourStart) * hourHeight;
                  const h = Math.max(30, (ev.endH - ev.startH) * hourHeight);
                  const w = `${100 / ev.totalCols}%`;
                  const left = `${(ev.col / ev.totalCols) * 100}%`;
                  return renderEventBlock(ev, { top, height: h, width: w, left }, false);
                })}
              </div>
            </div>
          </div>
        )}

        {/* empty: timed = 0 and allDay = 0 shouldn't happen but just in case */}
        {events.length === 0 && (
          <div className="day-panel__empty">
            {t("schedule.dayPanel.noEvents", "No pickups on this day")}
          </div>
        )}
      </div>
    </div>
  );
}

export default ScheduleDayPanel;
